import type { DiagnosticsService, DiagnosticsSnapshot } from "./diagnostics-service";

export interface DiagnosticsRuntimeContext {
  buildId: string;
  appVersion: string;
  renderScale: number;
  canvasBacking: { width: number; height: number } | null;
  storageMode: "persistent" | "memory";
  audioState: string;
  serviceWorkerState: string;
  lifecycleState: string;
  currentGame?: string | null;
}

export interface DiagnosticsReport {
  buildId: string;
  appVersion: string;
  environment: {
    userAgent: string;
    cssViewport: { width: number; height: number };
    visualViewport: { width: number; height: number } | null;
    screen: { width: number; height: number };
    devicePixelRatio: number;
    renderScale: number;
    canvasBacking: { width: number; height: number } | null;
    maxTouchPoints: number;
    gamepads: string[];
  };
  runtime: {
    storageMode: "persistent" | "memory";
    audioState: string;
    serviceWorkerState: string;
    lifecycleState: string;
    currentGame?: string | null;
  };
  metrics: DiagnosticsSnapshot;
}

function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

export function createDiagnosticsReport(
  scope: Window,
  diagnostics: DiagnosticsService,
  context: DiagnosticsRuntimeContext,
): DiagnosticsReport {
  const visual = scope.visualViewport;
  const gamepads = typeof scope.navigator.getGamepads === "function"
    ? Array.from(scope.navigator.getGamepads())
        .filter((gamepad): gamepad is Gamepad => gamepad !== null)
        .map((gamepad) => gamepad.id)
    : [];

  const runtime: DiagnosticsReport["runtime"] = {
    storageMode: context.storageMode,
    audioState: context.audioState,
    serviceWorkerState: context.serviceWorkerState,
    lifecycleState: context.lifecycleState,
  };
  if (context.currentGame !== undefined) runtime.currentGame = context.currentGame;

  return {
    buildId: context.buildId,
    appVersion: context.appVersion,
    environment: {
      userAgent: scope.navigator.userAgent,
      cssViewport: {
        width: finiteOr(scope.innerWidth, 0),
        height: finiteOr(scope.innerHeight, 0),
      },
      visualViewport: visual
        ? { width: finiteOr(visual.width, 0), height: finiteOr(visual.height, 0) }
        : null,
      screen: {
        width: finiteOr(scope.screen.width, 0),
        height: finiteOr(scope.screen.height, 0),
      },
      devicePixelRatio: finiteOr(scope.devicePixelRatio, 1),
      renderScale: context.renderScale,
      canvasBacking: context.canvasBacking ? { ...context.canvasBacking } : null,
      maxTouchPoints: scope.navigator.maxTouchPoints ?? 0,
      gamepads,
    },
    runtime,
    metrics: diagnostics.snapshot(),
  };
}

export function exportDiagnosticsJson(report: DiagnosticsReport): string {
  return JSON.stringify(report, null, 2);
}
